import React, { useRef } from 'react';

/**
 * Barra de pestañas de la casa, la misma que la Tienda de la app
 * (CORE-Front/lib/widgets/common/invoke_tab_bar.widget.dart): pastilla + icono +
 * rótulo, con subrayado en la pestaña activa.
 *
 * Escrita a mano y NO con las pestañas de Bootstrap: `bootstrap.min.js` se
 * importa global en src/index.js, y su plugin `data-bs-toggle="pill"` competiría
 * con el estado de React. Los dos patrones de pestañas que ya viven en el repo
 * usan ese plugin y ninguno de los dos declara bien el estado.
 *
 * Patrón Tabs de la APW: roving tabindex (solo la activa es tabulable), flechas
 * con vuelta circular, Home/End, y activación automática al mover el foco — que
 * aquí es lo correcto porque los dos paneles ya están montados y cambiar de
 * panel no dispara ninguna petición.
 *
 * No decide nada: la pestaña activa la mantiene quien la usa, igual que en la
 * app la mantiene la pantalla.
 */
const InvokeTabs = ({ tabs, active, onChange, ariaLabel, idPrefix, trailing }) => {
   const tabRefs = useRef([]);

   const handleKeyDown = (event) => {
      const current = tabs.findIndex((tab) => tab.key === active);
      let next = null;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      if (next === null) return;
      // Sin esto, las flechas mueven el foco Y hacen scroll de la página.
      event.preventDefault();
      onChange(tabs[next].key);
      const node = tabRefs.current[next];
      if (node) node.focus();
   };

   return (
      <div className="itab">
         <div className="itab__list" role="tablist" aria-label={ariaLabel} onKeyDown={handleKeyDown}>
            {tabs.map((tab, index) => {
               const isActive = tab.key === active;
               const Icon = tab.icon;
               return (
                  <button
                     key={tab.key}
                     type="button"
                     role="tab"
                     id={`${idPrefix}-tab-${tab.key}`}
                     aria-selected={isActive}
                     aria-controls={`${idPrefix}-panel-${tab.key}`}
                     tabIndex={isActive ? 0 : -1}
                     ref={(node) => {
                        tabRefs.current[index] = node;
                     }}
                     className={`itab__tab${isActive ? ' is-active' : ''}`}
                     onClick={() => onChange(tab.key)}
                  >
                     <span className="itab__pill">
                        {Icon ? <Icon className="itab__icon" size={18} aria-hidden="true" /> : null}
                        <span className="itab__label">{tab.label}</span>
                     </span>
                  </button>
               );
            })}
         </div>
         {trailing ? <div className="itab__trailing">{trailing}</div> : null}
      </div>
   );
};

export default InvokeTabs;
